import React, { useMemo } from "react"
import { makeStyles } from "@material-ui/core/styles"
import Screen from "shared/Components/Skeleton/Screen"
import { Typography, List, Grid, Divider } from "@material-ui/core"
import { useSelector } from "react-redux"
import { selectUser } from "shared/Modules/User/userSelectors"
import { useAppNavigation } from "shared/hooks/useAppNavigation"
import { faDoorOpen } from "@fortawesome/pro-regular-svg-icons"
import { AccountListItem, FormParagraph } from "shared/Modules/Login/Components/LoginForm"
import { AuthenticationAction } from "shared/Modules/Login/loginTypes"
import { Localized } from "@fluent/react"
import Box from "mobile/Components/Box"
import { useSwitchAccount } from "./useSwitchAcount"
import { StandardButton } from "shared/Components/Button/StandardButton"
import { Skeleton } from "@material-ui/lab"
import { StandardLoadingButton } from "shared/Components/Loading/LoadingButton"
import { LocalizedStrict } from "shared/Modules/Localization/Components/Localized"

const useStyles = makeStyles((theme) => ({
    companyList: {
        paddingTop: theme.spacing(3),
    },
    companiesWrapper: {
        marginBottom: theme.spacing(6),
    },
    switchDescriptionEmail: {
        color: "#54954b",
    },
    pendingConfirmSpinner: {
        marginLeft: "0.625rem",
    },
    confirmContent: {
        gap: "2rem",
    },
    pendingApprovalButton: {
        color: "rgba(0, 0, 0, 0.26)",
    },
    groupDivider: {
        marginTop: "0.5rem",
    },
}))

function SwitchAccount() {
    const { goToPrevious } = useAppNavigation()
    const user = useSelector(selectUser)
    const classes = useStyles()

    const {
        sendConfirmLink,
        onAccountClick,
        contactSupport,
        userData,
        isConfirmSend,
        isUserDataError,
        isUserDataLoading,
        selectedAccountName,
        hasAccounts,
    } = useSwitchAccount()

    const content = useMemo(() => {
        if (isUserDataLoading && !isConfirmSend) {
            return <Skeleton variant="rect" animation="wave" height={20} />
        }

        if (isUserDataError) {
            return (
                <>
                    <LocalizedStrict id="me-switch-account-failed-to-load">
                        <Typography variant="body1">Failed to load your user accounts.</Typography>
                    </LocalizedStrict>
                    <Localized id="me-switch-account-contact-support">
                        <StandardButton floating onClick={contactSupport}>
                            Contact support
                        </StandardButton>
                    </Localized>
                </>
            )
        }

        if (isConfirmSend && !userData?.userAccountGroups) {
            return (
                <Grid>
                    <LocalizedStrict id="me-switch-account-confirmation-send">
                        <Typography variant="body1">An email has been send. Please check your inbox.</Typography>
                    </LocalizedStrict>
                    <LocalizedStrict
                        id="me-switch-account-send-confirmation-submit-button"
                        attrs={{ loadingLabel: true }}
                    >
                        <StandardLoadingButton
                            floating
                            loading
                            loadingLabel="Pending approval..."
                            onClick={sendConfirmLink}
                            className={classes.pendingApprovalButton}
                        >
                            Pending approval...
                        </StandardLoadingButton>
                    </LocalizedStrict>
                </Grid>
            )
        }

        if (userData?.authenticationResult?.action === AuthenticationAction.APPROVE_ACCOUNT_FROM_URL) {
            return (
                <Box>
                    <Grid container className={classes.confirmContent}>
                        <Localized
                            id="me-switch-account-email-confirm"
                            vars={{ email: userData?.user.email }}
                            elems={{
                                email: <span className={classes.switchDescriptionEmail}>{userData?.user.email}</span>,
                            }}
                        >
                            <Typography variant="body1">
                                Before you can switch between user accounts, you must first confirm that you ovn the
                                email account{" "}
                                <span className={classes.switchDescriptionEmail}>{userData?.user.email}</span>
                            </Typography>
                        </Localized>
                        <Typography variant="body1">Click button below to send a confirmation link:</Typography>
                    </Grid>
                    <Localized id="me-switch-account-send-confirmation-link">
                        <StandardButton floating onClick={sendConfirmLink}>
                            Send confirmation link
                        </StandardButton>
                    </Localized>
                </Box>
            )
        }

        if (!hasAccounts) {
            return (
                <Localized
                    id="me-switch-account-no-accounts"
                    vars={{ email: userData?.user.email ?? "" }}
                    elems={{
                        email: <span className={classes.switchDescriptionEmail}>{userData?.user.email}</span>,
                    }}
                >
                    <Typography variant="body1">
                        No other accounts were found with the email address{" "}
                        <span className={classes.switchDescriptionEmail}>{userData?.user.email}</span>
                    </Typography>
                </Localized>
            )
        }

        return (
            <>
                <Localized
                    id="me-switch-account-description"
                    vars={{ email: user?.email ?? "" }}
                    elems={{ email: <span className={classes.switchDescriptionEmail}>{user?.email}</span> }}
                >
                    <Typography variant="body1">
                        You have the following accounts with the email address{" "}
                        <span className={classes.switchDescriptionEmail}>{user?.email}</span>
                    </Typography>
                </Localized>
                <List className={classes.companyList}>
                    {userData?.userAccountGroups?.map((group) => (
                        <div className={classes.companiesWrapper} key={group.line1}>
                            <Typography variant="h6">{group.line1}</Typography>
                            <Typography variant="body1" color="textSecondary">
                                {group.line2}
                            </Typography>
                            <Divider className={classes.groupDivider} />
                            {group.userAccounts.map((choice, index) => {
                                const loading = selectedAccountName === choice.user.username
                                return (
                                    <AccountListItem
                                        key={index}
                                        company={choice}
                                        loading={loading}
                                        disabled={loading}
                                        isAccountSelection
                                        onClick={() => onAccountClick(choice)}
                                    />
                                )
                            })}
                        </div>
                    ))}
                </List>
            </>
        )
    }, [isUserDataLoading, userData, isConfirmSend, selectedAccountName])

    return (
        <Localized id="me-switch-account-top-bar-title" attrs={{ primaryTopBarTitle: true }}>
            <Screen
                name="SwitchAccounts"
                onBackButtonPress={goToPrevious}
                primaryTopBarTitle="Betalingskort"
                showHeaderSection
                headerIcon={faDoorOpen}
            >
                {content}
            </Screen>
        </Localized>
    )
}

export default SwitchAccount
